import React, { useState } from 'react';
import './PoolCreationModal.css';

const PoolCreationModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    strategy: '',
    riskLevel: 'medium',
    managementFee: 2.0,
    performanceFee: 20.0,
    minInvestment: 0.1,
    maxInvestment: 100,
    withdrawalNoticeDays: 7,
    maxPoolSize: 1000
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const riskLevels = [
    { value: 'low', label: 'Low Risk', description: 'Conservative strategies, lower volatility' },
    { value: 'medium', label: 'Medium Risk', description: 'Balanced approach, moderate volatility' },
    { value: 'high', label: 'High Risk', description: 'Aggressive strategies, higher potential returns' }
  ];

  const strategyTemplates = [
    {
      name: 'Conservative Growth',
      description: 'Focus on established tokens with steady growth potential',
      riskLevel: 'low'
    },
    {
      name: 'Balanced Portfolio',
      description: 'Mix of established and emerging tokens with risk management',
      riskLevel: 'medium'
    },
    {
      name: 'Growth Momentum',
      description: 'Target high-growth potential tokens with active trading',
      riskLevel: 'medium'
    },
    {
      name: 'Aggressive Trading',
      description: 'High-frequency trading with leverage and advanced strategies',
      riskLevel: 'high'
    },
    {
      name: 'DeFi Yield Farming',
      description: 'Focus on DeFi protocols and yield generation opportunities',
      riskLevel: 'high'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleStrategyTemplateSelect = (template) => {
    setFormData(prev => ({
      ...prev,
      strategy: template.description,
      riskLevel: template.riskLevel
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Pool name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Pool description is required';
    }

    if (!formData.strategy.trim()) {
      newErrors.strategy = 'Investment strategy is required';
    }

    if (formData.managementFee < 0 || formData.managementFee > 10) {
      newErrors.managementFee = 'Management fee must be between 0% and 10%';
    }

    if (formData.performanceFee < 0 || formData.performanceFee > 50) {
      newErrors.performanceFee = 'Performance fee must be between 0% and 50%';
    }

    if (formData.minInvestment <= 0) {
      newErrors.minInvestment = 'Minimum investment must be greater than 0';
    }

    if (formData.maxInvestment <= formData.minInvestment) {
      newErrors.maxInvestment = 'Maximum investment must be greater than minimum';
    }

    if (formData.maxPoolSize <= 0) {
      newErrors.maxPoolSize = 'Maximum pool size must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting pool creation:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="pool-creation-modal">
        <div className="modal-header">
          <h2>Create New Investment Pool</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="pool-creation-form">
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label htmlFor="name">Pool Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="e.g., Solana Growth Fund"
                className={errors.name ? 'error' : ''}
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe your pool's investment focus and goals"
                rows={3}
                className={errors.description ? 'error' : ''}
              />
              {errors.description && <span className="error-text">{errors.description}</span>}
            </div>
          </div>

          {/* Strategy Selection */}
          <div className="form-section">
            <h3>Investment Strategy</h3>
            
            <div className="strategy-templates">
              <label>Strategy Templates (Optional)</label>
              <div className="templates-grid">
                {strategyTemplates.map((template, index) => (
                  <div 
                    key={index}
                    className="strategy-template"
                    onClick={() => handleStrategyTemplateSelect(template)}
                  >
                    <div className="template-name">{template.name}</div>
                    <div className="template-description">{template.description}</div>
                    <div className={`template-risk ${template.riskLevel}`}>
                      {template.riskLevel.toUpperCase()} RISK
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="strategy">Custom Strategy Description *</label>
              <textarea
                id="strategy"
                name="strategy"
                value={formData.strategy}
                onChange={handleInputChange}
                placeholder="Describe your detailed investment strategy, approach, and methodology"
                rows={4}
                className={errors.strategy ? 'error' : ''}
              />
              {errors.strategy && <span className="error-text">{errors.strategy}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="riskLevel">Risk Level *</label>
              <select
                id="riskLevel"
                name="riskLevel"
                value={formData.riskLevel}
                onChange={handleInputChange}
              >
                {riskLevels.map(level => (
                  <option key={level.value} value={level.value}>
                    {level.label} - {level.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Fee Structure */}
          <div className="form-section">
            <h3>Fee Structure</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="managementFee">Management Fee (% annually) *</label>
                <input
                  type="number"
                  id="managementFee"
                  name="managementFee"
                  value={formData.managementFee}
                  onChange={handleInputChange}
                  min="0"
                  max="10"
                  step="0.1"
                  className={errors.managementFee ? 'error' : ''}
                />
                {errors.managementFee && <span className="error-text">{errors.managementFee}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="performanceFee">Performance Fee (% of profits) *</label>
                <input
                  type="number"
                  id="performanceFee"
                  name="performanceFee"
                  value={formData.performanceFee}
                  onChange={handleInputChange}
                  min="0"
                  max="50"
                  step="0.1"
                  className={errors.performanceFee ? 'error' : ''}
                />
                {errors.performanceFee && <span className="error-text">{errors.performanceFee}</span>}
              </div>
            </div>
          </div>

          {/* Investment Limits */}
          <div className="form-section">
            <h3>Investment Limits</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="minInvestment">Minimum Investment (SOL) *</label>
                <input
                  type="number"
                  id="minInvestment"
                  name="minInvestment"
                  value={formData.minInvestment}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  className={errors.minInvestment ? 'error' : ''}
                />
                {errors.minInvestment && <span className="error-text">{errors.minInvestment}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="maxInvestment">Maximum Investment per User (SOL) *</label>
                <input
                  type="number"
                  id="maxInvestment"
                  name="maxInvestment"
                  value={formData.maxInvestment}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  className={errors.maxInvestment ? 'error' : ''}
                />
                {errors.maxInvestment && <span className="error-text">{errors.maxInvestment}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="maxPoolSize">Maximum Pool Size (SOL) *</label>
                <input
                  type="number"
                  id="maxPoolSize"
                  name="maxPoolSize"
                  value={formData.maxPoolSize}
                  onChange={handleInputChange}
                  min="1"
                  step="1"
                  className={errors.maxPoolSize ? 'error' : ''}
                />
                {errors.maxPoolSize && <span className="error-text">{errors.maxPoolSize}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="withdrawalNoticeDays">Withdrawal Notice (Days)</label>
                <input
                  type="number"
                  id="withdrawalNoticeDays"
                  name="withdrawalNoticeDays"
                  value={formData.withdrawalNoticeDays}
                  onChange={handleInputChange}
                  min="0"
                  max="30"
                />
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="submit" 
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating Pool...' : 'Create Pool'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PoolCreationModal;